import * as React from 'react';

import { config } from '~/config';

interface VersionResponse {
  version: string;
}

interface UseApiHealthReturn {
  /** Whether the API is currently healthy (responding successfully) */
  isHealthy: boolean;

  /** The API version from the last successful health check */
  apiVersion: string | null;

  /** Whether we're currently checking health (initial check only) */
  isChecking: boolean;
}

/**
 * Hook to monitor API health by polling the /version endpoint.
 *
 * Polls every 30 seconds and tracks health status and API version.
 */
export function useApiHealth(): UseApiHealthReturn {
  const [isHealthy, setIsHealthy] = React.useState(true);
  const [apiVersion, setApiVersion] = React.useState<string | null>(null);
  const [isChecking, setIsChecking] = React.useState(true);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const checkHealth = React.useCallback(async () => {
    try {
      const response = await fetch(`${config.api.url}/version`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });

      if (response.ok) {
        const data = (await response.json()) as VersionResponse;
        setIsHealthy(true);
        setApiVersion(data.version);
      } else {
        setIsHealthy(false);
      }
    } catch {
      setIsHealthy(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  React.useEffect(() => {
    checkHealth();

    intervalRef.current = setInterval(checkHealth, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [checkHealth]);

  return {
    isHealthy,
    apiVersion,
    isChecking,
  };
}
